import { api } from "@signet/web-kit";
import type { Step, AccountStatus, BanReason } from "../../shared/lifecycle.ts";
import type { Results } from "../../shared/results.ts";
import type { SupplierResult } from "../../shared/suppliers.ts";
import type { AppealState } from "../../shared/appeal.ts";
import type { PhoneRow } from "../../shared/phones.ts";

/* ──────────────────────────────────────────────────────────────────────────
   What the Accounts tab reads from /api/m/igsuicide/stock, typed once. The
   shapes mirror server/accounts/store.ts, written out by hand because the
   web half never imports the server half; the pure rules (steps, results,
   reading a file) live in ../../shared and are imported by both.
   ────────────────────────────────────────────────────────────────────────── */

export const STOCK = "/api/m/igsuicide/stock";

export type AccountType = "aged" | "soft_reg" | "unmarked";

export type Stock = {
  id: string;
  username: string;
  link: string;
  supplier: string;
  accountType: AccountType;
  classification: "professional" | "personal";
  category: string | null;
  pfp: "photo" | "colour";
  warmup: number;
  bio: string | null;
  phone: string | null;
  poster: string | null;
  posterName: string | null;
  creator: string | null;
  takenOn: string | null;
  warmupDoneOn: string | null;
  linkAddedOn: string | null;
  linkFlaggedOn: string | null;
  status: AccountStatus;
  statusOn: string | null;
  hasTwoFactor: boolean;
  followers: number | null;
  views: number | null;
  missingSince: string | null;
  lastReadAt: string | null;
  step: Step;
  /** The date each warm-up and posting day was ticked, in day order. */
  /** Brought in off the IG SUICIDE sheet (migration 0006): no login, type left unmarked. */
  imported: boolean;
  ticks: { warmup: (string | null)[]; posting: (string | null)[] };
  /** The address in the bio, when Signet made it. */
  linkUrl: string | null;
  /** Not posting since (Jay, 2026-10-12). */
  pausedOn: string | null;
  banReason: BanReason | null;
  /** The flagged link went into a Story Highlight on this day. */
  highlightOn: string | null;
  /** Times one of its links was flagged, over its whole life. */
  flagCount: number;
  importedActive?: boolean;
  /** A 3 reason ban's appeal (2026-10-15), and the day of the ban it is for. */
  appealState: AppealState | null;
  appealFrom: string | null;
  /** A connected ban: the 3 reason ban that took it down, when known. */
  causedBy: { id: string; username: string; appealState: AppealState | null } | null;
  /** Lost for good, off its phone and in the Graveyard (shared/appeal.ts offPhone). */
  offPhone: boolean;
  /** Added by hand with no warm-up needed (2026-10-21). */
  warmupSkipped?: boolean;
  lastNote: AppealNote | null;
  /** The poster's own note on it (2026-10-22). */
  note: string | null;
  /** A 3 reason ban: the day it was confirmed logged out of the phone (2026-10-22). */
  removedOn: string | null;
};

export type AppealNote = { id?: string; note: string | null; state: AppealState | null; at: string; by: string | null };

/** What the Banned dialog sends. */
export type Ban = { reason: BanReason; on: string; causedBy?: string | null; note?: string | null };

export type Phone = { label: string; accountType: AccountType; accounts: number };

export type Poster = { id: string; username: string; name: string };
export type StockList = { today: string; me: string; manages: boolean; accounts: Stock[]; phones: Phone[]; allPhones: PhoneRow[]; posters: Poster[] };

export type Login = {
  username: string; password: string | null; email: string | null; emailPassword: string | null;
  backupEmail: string | null; backupEmailPassword: string | null; other: string[]; hasTwoFactor: boolean;
};

export type Bio = { id: string; text: string; used: number };

export type UploadRow = {
  username: string; password: string | null; email: string | null; emailPassword: string | null;
  backupEmail: string | null; backupEmailPassword: string | null; twoFactor: string | null; other: string[];
};

export type StepName = "link_added" | "link_flagged" | "highlight" | "paused" | "banned" | "active";

export const stockKeys = {
  all: ["igsuicide", "stock"] as const,
  list: (scope: string) => ["igsuicide", "stock", "list", scope] as const,
  results: ["igsuicide", "stock", "results"] as const,
  bios: ["igsuicide", "stock", "bios"] as const,
};

export const fetchStock = () => api<StockList>(STOCK);
export const fetchPhonesOf = (poster: string) => api<{ phones: Phone[] }>(`${STOCK}/phones?${new URLSearchParams({ poster })}`);
/** As the server saves it: "Phone 7" is "7". */
export const phoneLabel = (typed: string) => typed.trim().toUpperCase().replace(/^PHONE\s*/, "").trim();
/** The active creators in Creators, the only ones an account can be taken for (Jay, 2026-10-19). */
export const fetchStockCreators = () => api<{ creators: string[]; known: boolean }>(`${STOCK}/creators`);
export const fetchPosterChoices = () => api<{ people: Poster[] }>(`${STOCK}/posters/choices`);
export const savePosters = (ids: string[]) => api<{ posters: Poster[] }>(`${STOCK}/posters`, { method: "PUT", body: { ids } });
export const fetchResults = () => api<Results & { suppliers: SupplierResult[] }>(`${STOCK}/results`);
export const fetchBios = () => api<{ bios: Bio[] }>(`${STOCK}/bios`);
export const fetchLogin = (id: string) => api<Login>(`${STOCK}/${encodeURIComponent(id)}/login`);
export const saveLogin = (id: string, body: { username?: string; password?: string | null }) =>
  api<{ ok: true; username: string }>(`${STOCK}/${encodeURIComponent(id)}/login`, { method: "PATCH", body });
export const fetchCode = (id: string) => api<{ code: string; secondsLeft: number }>(`${STOCK}/${encodeURIComponent(id)}/code`);

export const uploadStock = (body: { link: string; accountType: AccountType; filename: string | null; rows: UploadRow[] }) =>
  api<{ added: number; skipped: string[] }>(`${STOCK}/upload`, { body });
/** An empty phone; it takes its type from the first account put on it (Jay, 2026-10-20). */
export const createPhone = (body: { phone: string; poster?: string }) => api<{ label: string }>(`${STOCK}/phones`, { body });
export type AddAccount = {
  username: string; password: string | null; email: string | null; emailPassword: string | null; twoFactor: string | null;
  phone: string; creator: string; poster?: string; accountType: Exclude<AccountType, "unmarked"> | null; warmup: boolean;
};
/** `takenBackFrom` is set when the username was a left creator's account, moved over rather than added. */
export const addStock = (body: AddAccount) => api<{ id: string; phone: string; accountType: AccountType; takenBackFrom?: string }>(`${STOCK}/add`, { body });
export const takeStock = (id: string, body: { phone: string; creator: string; poster?: string }) => api(`${STOCK}/${encodeURIComponent(id)}/take`, { body });
export const stepStock = (id: string, step: StepName, ban?: Ban) => api(`${STOCK}/${encodeURIComponent(id)}/step`, { body: { step, ...ban } });
export const appealUpdate = (id: string, body: { state?: AppealState | null; note?: string | null; bringBack?: string[] }) => api(`${STOCK}/${encodeURIComponent(id)}/appeal`, { body });
export const saveNote = (id: string, note: string) => api(`${STOCK}/${encodeURIComponent(id)}/note`, { body: { note } });
export const savePhoneNote = (body: { poster: string; phone: string; note: string }) => api(`${STOCK}/phones/note`, { body });
/** The creator for each of a phone's next accounts, in order (Ben, 2026-10-28). */
export const savePhonePlan = (body: { poster: string; phone: string; creators: string[] }) => api<{ plan: string[] }>(`${STOCK}/phones/plan`, { body });
export const setRemoved = (id: string, removed: boolean) => api(`${STOCK}/${encodeURIComponent(id)}/removed`, { body: { removed } });
export const fetchAppealNotes = (id: string) => api<{ notes: AppealNote[] }>(`${STOCK}/${encodeURIComponent(id)}/appeal`);
export const tickStock = (id: string) => api<{ phase: "warmup" | "posting"; day: number }>(`${STOCK}/${encodeURIComponent(id)}/tick`, { body: {} });
export const untickStock = (id: string, from?: { phase: "warmup" | "posting"; day: number }) => api(`${STOCK}/${encodeURIComponent(id)}/untick`, { body: from ?? {} });

export type LinkSource = { category: string; source: { label: string; url: string; host: string; slug: string } | null; ideas: string[] };
export const fetchLinkSource = (id: string, ideas?: { current: string; page: number }) =>
  api<LinkSource>(`${STOCK}/${encodeURIComponent(id)}/link-source${ideas ? `?${new URLSearchParams({ current: ideas.current, page: String(ideas.page) })}` : ""}`);
export const addLink = (id: string, slug: string | null) => api<{ ok: true; url: string | null }>(`${STOCK}/${encodeURIComponent(id)}/link`, { body: slug ? { slug } : {} });
export const unlinkStock = (id: string) => api(`${STOCK}/${encodeURIComponent(id)}/unlink`, { body: {} });
export const releaseStock = (id: string) => api(`${STOCK}/${encodeURIComponent(id)}/release`, { body: {} });
/** Moves the account to another active creator (Jay, 2026-10-28). */
export const saveCreator = (id: string, creator: string) => api(`${STOCK}/${encodeURIComponent(id)}`, { method: "PATCH", body: { creator } });
export const deleteStock =(id: string) => api(`${STOCK}/${encodeURIComponent(id)}`, { method: "DELETE" });
export const addBios = (texts: string[]) => api<{ added: number }>(`${STOCK}/bios`, { body: { texts } });
export const removeBio = (id: string) => api(`${STOCK}/bios/${encodeURIComponent(id)}`, { method: "DELETE" });

export const TYPE_LABEL: Record<AccountType, string> = { aged: "Aged", soft_reg: "Soft reg", unmarked: "No type yet" };

/** "Professional: Entertainer", "Personal account". */
export const classLabel = (a: Pick<Stock, "classification" | "category">) => (a.classification === "professional" ? `Professional: ${a.category ?? ""}` : "Personal account");
export const pfpLabel = (pfp: Stock["pfp"]) => (pfp === "photo" ? "Creator photo as profile pic" : "Solid colour profile pic");

/** "7 Oct". */
export const shortDate = (day: string) => new Date(`${day}T00:00:00`).toLocaleDateString(undefined, { day: "numeric", month: "short" });
